import React, {Component} from 'react'
import {
  Box,
  Button,
  Heading,
  VStack,
  Grid,
  GridItem,
  Text,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Spinner,
  Badge,
  Divider,
  List,
  ListItem,
  Avatar
} from '@chakra-ui/react';
import { ViewIcon, ChatIcon, EmailIcon } from '@chakra-ui/icons';
import './Dashboard.css'
import Sidebar from '../Sidebar/Sidebar';
import Navigation from '../../Navigation/Navigation';
import MobileMenu from '../MobileMenu/MobileMenu';
import Footer from '../../../components/Footer/Footer';
import colors from '../../../theme/colors';

// API URL from environment variable with fallback to localhost
const API_URL = process.env.REACT_APP_API_URL || '';

class Dashboard extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isMobileMenuOpen: false,
      dashboardMetrics: null,
      loadingMetrics: true,
      recentSuggestions: [],
      loadingRecent: true
    };
  }

  componentDidMount() {
    // Load all necessary data for dashboard
    this.loadDashboardMetrics();
    this.loadRecentSuggestions();

    // Load trending topics and summaries if handlers exist
    if (this.props.loadTrendingTopics) {
      this.props.loadTrendingTopics();
    }
    if (this.props.loadSummaries) {
      this.props.loadSummaries();
    }
    if (this.props.loadAllSuggestions) {
      this.props.loadAllSuggestions();
    }
    if (this.props.loadProblems) {
      this.props.loadProblems();
    }
  }

  loadDashboardMetrics = async () => {
    this.setState({ loadingMetrics: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/dashboard/metrics`, {
        method: 'GET'
      });

      const data = await response.json();

      if (data.success) {
        this.setState({
          dashboardMetrics: data.result,
          loadingMetrics: false
        });
      } else {
        console.error('Failed to load dashboard metrics:', data.message);
        this.setState({ loadingMetrics: false });
      }
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
      this.setState({ loadingMetrics: false });
    }
  }

  loadRecentSuggestions = async () => {
    this.setState({ loadingRecent: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/suggestions/recent?limit=5`, {
        method: 'GET'
      });

      const data = await response.json();

      if (data.success) {
        this.setState({
          recentSuggestions: data.result.suggestions || [],
          loadingRecent: false
        });
      } else {
        console.error('Failed to load recent suggestions:', data.message);
        this.setState({ loadingRecent: false });
      }
    } catch (error) {
      console.error('Error loading recent suggestions:', error);
      this.setState({ loadingRecent: false });
    }
  }

  getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Nu';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}u`;
    return `${diffDays}d`;
  }

  truncateText = (text, maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  getPriorityColor = (level) => {
    switch(level) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  }

  getPriorityIcon = (level) => {
    switch(level) {
      case 'high': return '🚨';
      case 'medium': return '⚠️';
      case 'low': return '📝';
      default: return '📋';
    }
  }

  handleOpenMobileMenu = () => {
    this.setState({ isMobileMenuOpen: true })
  }

  handleCloseMobileMenu = () => {
    this.setState({ isMobileMenuOpen: false })
  }

  componentWillUnmount() {
    // Ensure mobile menu is closed when component unmounts
    if (this.state.isMobileMenuOpen) {
      this.setState({ isMobileMenuOpen: false })
    }
  }

  render () {
    const { dashboardMetrics, loadingMetrics, recentSuggestions, loadingRecent } = this.state;
    const trendingTopics = this.props.state.trendingTopics || { items: [], loading: false };
    const problems = this.props.state.problems || { items: [], loading: false };
    const allSuggestionsCount = this.props.state.allSuggestions?.total || 0;
    const summariesCount = this.props.state.summaries?.items?.length || 0;

    return (
      <>
        <Grid templateColumns="repeat(24, 1fr)" bg="gray.50">
          {/* Sidebar */}
          <GridItem
            as="aside"
            colSpan={{ base: 0, md: 4, lg: 3 }}
          display={{ base: "none", md: "block" }}
            bg="gray.50"
            minHeight={{ lg: '100vh' }}
            p={0}
          >
            <Sidebar
              getFeed={this.props.getFeed}
              onRouteChange={this.props.onRouteChange}
              userVersion={this.props.state.userVersion}
              resetSuggestionsPage={this.props.resetSuggestionsPage}
              onLogout={this.props.onLogout}
            />
          </GridItem>

          {/* Main Content */}
          <GridItem
            as="main"
            colSpan={{ base: 24, md: 20, lg: 21 }}
            p={{ base: "8px", md: "10px" }}
            display="flex"
            flexDirection="column"
            minHeight="100vh"
          >
            <Navigation
              state={this.props.state}
              onRouteChange={this.props.onRouteChange}
              getFeed={this.props.getFeed}
              onVersionChange={this.props.onVersionChange}
              onLogout={this.props.onLogout}
              onOpenFeedback={this.props.onOpenFeedback}
              onOpenMobileMenu={this.handleOpenMobileMenu}
            />

            <Box p={{ base: "16px", md: "40px" }} flex="1">
              <VStack spacing={8} align="stretch">
                {/* Header */}
                <Box textAlign="center">
                  <Heading as="h1" size={{ base: "lg", md: "xl" }} color={colors.primary.main} mb={2}>
                    Dashboard - Welkom terug, {this.props.state.username}!
                  </Heading>
                  <Text color="gray.600" fontSize={{ base: "md", md: "lg" }}>
                    Overzicht van input van inwoners
                  </Text>
                </Box>

                {/* Metrics Cards */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                  <Card bg="white" shadow="lg" borderTop="4px solid" borderTopColor={colors.primary.main}>
                    <CardBody textAlign="center">
                      <Stat>
                        <StatLabel color="gray.600">Totaal Suggesties</StatLabel>
                        <StatNumber fontSize="3xl" color={colors.primary.main}>
                          {loadingMetrics ? <Spinner size="md" color={colors.primary.main} /> : allSuggestionsCount}
                        </StatNumber>
                        {dashboardMetrics?.new_suggestions_today > 0 && (
                          <StatHelpText>
                            <StatArrow type="increase" />
                            {dashboardMetrics.new_suggestions_today} vandaag
                          </StatHelpText>
                        )}
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg="white" shadow="lg" borderTop="4px solid" borderTopColor="green.400">
                    <CardBody textAlign="center">
                      <Stat>
                        <StatLabel color="gray.600">Deze Week</StatLabel>
                        <StatNumber fontSize="3xl" color="green.600">
                          {loadingMetrics ? <Spinner size="md" /> : (dashboardMetrics?.new_suggestions_week || 0)}
                        </StatNumber>
                        {dashboardMetrics?.week_change !== undefined && (
                          <StatHelpText>
                            <StatArrow type={dashboardMetrics.week_change >= 0 ? 'increase' : 'decrease'} />
                            {Math.abs(dashboardMetrics.week_change)} vs vorige week
                          </StatHelpText>
                        )}
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg="white" shadow="lg" borderTop="4px solid" borderTopColor="purple.400">
                    <CardBody textAlign="center">
                      <Stat>
                        <StatLabel color="gray.600">Ongelezen Berichten</StatLabel>
                        <StatNumber fontSize="3xl" color="purple.600">
                          {loadingMetrics ? <Spinner size="md" /> : (dashboardMetrics?.unread_messages || 0)}
                        </StatNumber>
                        <StatHelpText>
                          {dashboardMetrics?.unread_messages > 0 ? 'Actie vereist' : 'Alles bijgewerkt'}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg="white" shadow="lg" borderTop="4px solid" borderTopColor="orange.400">
                    <CardBody textAlign="center">
                      <Stat>
                        <StatLabel color="gray.600">Trending Topics</StatLabel>
                        <StatNumber fontSize="3xl" color="orange.600">
                          {trendingTopics.loading ? <Spinner size="md" /> : (trendingTopics.items?.length || 0)}
                        </StatNumber>
                        <StatHelpText>
                          {trendingTopics.items?.length > 0 ? 'Geïdentificeerd' : 'Geen trends'}
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* Trending Topics Section */}
                <Card bg="white" shadow="md">
                  <CardHeader>
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Heading size="md" color="gray.700">
                          Suggesties per Thema
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                          Meest urgente onderwerpen op basis van keyword-analyse
                        </Text>
                      </VStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => this.props.onRouteChange('overzicht')}
                      >
                        Bekijk alles →
                      </Button>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    {trendingTopics.loading ? (
                      <Box textAlign="center" py={8}>
                        <Spinner size="lg" color="blue.500" />
                        <Text mt={4} color="gray.500">Trending topics laden...</Text>
                      </Box>
                    ) : trendingTopics.items && trendingTopics.items.length > 0 ? (
                      <VStack spacing={3} align="stretch">
                        {trendingTopics.items.slice(0, 3).map((topic, index) => (
                          <Card
                            key={index}
                            bg="neutral.50"
                            shadow="sm"
                            borderLeft="4px solid"
                            borderLeftColor={`${this.getPriorityColor(topic.priority_level)}.400`}
                            _hover={{ bg: 'blue.50', cursor: 'pointer', transform: 'translateY(-1px)' }}
                            transition="all 0.2s ease"
                            onClick={() => this.props.onTrendingTopicFilter && this.props.onTrendingTopicFilter(topic)}
                          >
                            <CardBody p={4}>
                              <HStack spacing={4} align="start">
                                <Badge
                                  bg={colors.rankBadge.bg}
                                  color={colors.rankBadge.text}
                                  variant="solid"
                                  borderRadius="full"
                                  minW="30px"
                                  h="30px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  fontSize="sm"
                                  fontWeight="bold"
                                >
                                  #{index + 1}
                                </Badge>

                                <VStack align="start" spacing={2} flex="1">
                                  <HStack spacing={3}>
                                    <Heading size="sm" color="gray.700">
                                      {this.getPriorityIcon(topic.priority_level)} {topic.topic}
                                    </Heading>
                                    <Badge
                                      colorScheme={this.getPriorityColor(topic.priority_level)}
                                      variant="outline"
                                      fontSize="xs"
                                      textTransform="uppercase"
                                    >
                                      {topic.priority_level}
                                    </Badge>
                                  </HStack>

                                  <Text fontSize="sm" color="gray.600">
                                    {this.truncateText(topic.summary, 100)}
                                  </Text>

                                  <HStack spacing={4} fontSize="xs" color="gray.500">
                                    <Text fontWeight="semibold">
                                      {topic.people_count} {topic.people_count === 1 ? 'persoon' : 'personen'}
                                    </Text>
                                    <Text>•</Text>
                                    <Text fontWeight="semibold">
                                      {topic.suggestion_count} {topic.suggestion_count === 1 ? 'suggestie' : 'suggesties'}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </HStack>
                            </CardBody>
                          </Card>
                        ))}
                        {trendingTopics.items.length > 3 && (
                          <Button
                            variant="outline"
                            colorScheme="blue"
                            size="sm"
                            onClick={() => this.props.onRouteChange('overzicht')}
                          >
                            Bekijk alle {trendingTopics.items.length} trending topics
                          </Button>
                        )}
                      </VStack>
                    ) : (
                      <Box textAlign="center" py={8} color="gray.500">
                        <Text>Geen trending topics gevonden</Text>
                        <Text fontSize="sm" mt={2}>Er zijn nog niet genoeg suggesties om trends te identificeren</Text>
                      </Box>
                    )}
                  </CardBody>
                </Card>

                {/* Problemen Section */}
                <Card bg="white" shadow="md">
                  <CardHeader>
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Heading size="md" color="gray.700">
                          Problemen
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                          Onderliggende problemen, samengevoegd door AI en gewogen naar hoeveel inwoners erover klagen
                        </Text>
                      </VStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => this.props.onRouteChange('overzicht')}
                      >
                        Bekijk alles →
                      </Button>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    {problems.loading ? (
                      <Box textAlign="center" py={8}>
                        <Spinner size="lg" color="blue.500" />
                        <Text mt={4} color="gray.500">Problemen analyseren...</Text>
                      </Box>
                    ) : problems.items && problems.items.length > 0 ? (
                      <VStack spacing={3} align="stretch">
                        {problems.items.slice(0, 5).map((problem, index) => (
                          <Card
                            key={index}
                            bg="neutral.50"
                            shadow="sm"
                            borderLeft="4px solid"
                            borderLeftColor={`${this.getPriorityColor(problem.priority_level)}.400`}
                            _hover={{ bg: 'blue.50', cursor: 'pointer', transform: 'translateY(-1px)' }}
                            transition="all 0.2s ease"
                            onClick={() => this.props.onProblemSelect && this.props.onProblemSelect(problem)}
                          >
                            <CardBody p={4}>
                              <HStack spacing={4} align="start">
                                <Badge
                                  bg={colors.rankBadge.bg}
                                  color={colors.rankBadge.text}
                                  variant="solid"
                                  borderRadius="full"
                                  minW="30px"
                                  h="30px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  fontSize="sm"
                                  fontWeight="bold"
                                >
                                  #{index + 1}
                                </Badge>

                                <VStack align="start" spacing={2} flex="1">
                                  <Heading size="sm" color="gray.700">
                                    {problem.title}
                                  </Heading>

                                  {problem.description && (
                                    <Text fontSize="sm" color="gray.600">
                                      {this.truncateText(problem.description, 140)}
                                    </Text>
                                  )}

                                  <HStack spacing={4} fontSize="xs" color="gray.500">
                                    <Badge colorScheme="orange" variant="subtle">
                                      Gewicht: {problem.weight}
                                    </Badge>
                                    <Text fontWeight="semibold">
                                      {problem.weight} {problem.weight === 1 ? 'persoon' : 'personen'}
                                    </Text>
                                    <Text>•</Text>
                                    <Text fontWeight="semibold">
                                      {problem.suggestion_count} {problem.suggestion_count === 1 ? 'suggestie' : 'suggesties'}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </HStack>
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    ) : (
                      <Box textAlign="center" py={8} color="gray.500">
                        <Text>Geen problemen geïdentificeerd</Text>
                        <Text fontSize="sm" mt={2}>Er zijn nog niet genoeg suggesties om onderliggende problemen te bepalen</Text>
                      </Box>
                    )}
                  </CardBody>
                </Card>

                {/* Recent Activity Section */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  {/* Recent Suggestions */}
                  <Card bg="white" shadow="md">
                    <CardHeader>
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <Heading size="md" color="gray.700">
                            Recente Suggesties
                          </Heading>
                          <Text fontSize="sm" color="gray.500">
                            Laatst ingediende input van inwoners
                          </Text>
                        </VStack>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => this.props.onRouteChange('alleSuggesties')}
                        >
                          Zie alle →
                        </Button>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0}>
                      {loadingRecent ? (
                        <Box textAlign="center" py={8}>
                          <Spinner size="md" color="blue.500" />
                        </Box>
                      ) : recentSuggestions.length > 0 ? (
                        <List spacing={3}>
                          {recentSuggestions.map((suggestion) => (
                            <ListItem
                              key={suggestion.id}
                              p={3}
                              bg="neutral.50"
                              borderRadius="md"
                              _hover={{ bg: 'blue.50', cursor: 'pointer' }}
                              transition="all 0.2s"
                              onClick={() => {
                                if (this.props.onSuggestionSelect) {
                                  this.props.onSuggestionSelect(suggestion);
                                }
                              }}
                            >
                              <HStack spacing={3} align="start">
                                <Avatar size="sm" name={suggestion.username} bg={colors.primary.main} />
                                <VStack align="start" spacing={1} flex="1">
                                  <HStack spacing={2}>
                                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                                      {suggestion.username}
                                    </Text>
                                    <Text fontSize="xs" color="gray.400">
                                      {this.getTimeAgo(suggestion.created_at)}
                                    </Text>
                                  </HStack>
                                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                    {this.truncateText(suggestion.text, 100)}
                                  </Text>
                                  <Badge
                                    colorScheme={
                                      suggestion.status === 'reviewed' ? 'yellow' : 'green'
                                    }
                                    fontSize="xs"
                                  >
                                    {suggestion.status === 'submitted' ? 'Ingediend' : 'Bekeken'}
                                  </Badge>
                                </VStack>
                              </HStack>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box textAlign="center" py={8} color="gray.500">
                          <Text>Geen recente suggesties</Text>
                        </Box>
                      )}
                    </CardBody>
                  </Card>

                  {/* Quick Stats / Analytics */}
                  <Card bg="white" shadow="md">
                    <CardHeader>
                      <Heading size="md" color="gray.700">
                        Analyse Overzicht
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        Status van ingediende suggesties
                      </Text>
                    </CardHeader>
                    <CardBody pt={0}>
                      {loadingMetrics ? (
                        <Box textAlign="center" py={8}>
                          <Spinner size="md" color="blue.500" />
                        </Box>
                      ) : (
                        <VStack spacing={4} align="stretch">
                          <Box>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                                Ingediend
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                {dashboardMetrics?.status_breakdown?.submitted || 0}
                              </Text>
                            </HStack>
                            <Box
                              h="8px"
                              bg="gray.200"
                              borderRadius="full"
                              overflow="hidden"
                            >
                              <Box
                                h="100%"
                                bg="gray.500"
                                w={`${((dashboardMetrics?.status_breakdown?.submitted || 0) / allSuggestionsCount * 100)}%`}
                                borderRadius="full"
                              />
                            </Box>
                          </Box>

                          <Box>
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                                Beoordeeld
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color={colors.primary.main}>
                                {dashboardMetrics?.status_breakdown?.reviewed || 0}
                              </Text>
                            </HStack>
                            <Box
                              h="8px"
                              bg="gray.200"
                              borderRadius="full"
                              overflow="hidden"
                            >
                              <Box
                                h="100%"
                                bg={colors.primary.main}
                                w={`${((dashboardMetrics?.status_breakdown?.reviewed || 0) / allSuggestionsCount * 100)}%`}
                                borderRadius="full"
                              />
                            </Box>
                          </Box>

                          <Divider my={2} />

                          <Box bg="gray.50" p={4} borderRadius="md">
                            <VStack spacing={2} align="stretch">
                              <Text fontSize="xs" fontWeight="semibold" color="gray.700" textTransform="uppercase">
                                AI Overzichten
                              </Text>
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="gray.600">
                                  Opgeslagen analyses
                                </Text>
                                <Text fontSize="lg" fontWeight="bold" color={colors.primary.main}>
                                  {summariesCount}
                                </Text>
                              </HStack>
                              <Button
                                size="sm"
                                bg={colors.primary.main}
                                color="white"
                                _hover={{ bg: colors.primary.dark }}
                                onClick={() => this.props.onRouteChange('overzicht')}
                                leftIcon={<ViewIcon />}
                              >
                                Bekijk overzichten
                              </Button>
                            </VStack>
                          </Box>
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* Quick Actions */}
                <Card bg="gradient" shadow="md">
                  <CardHeader>
                    <Heading size="md" color="gray.700" textAlign="center">
                      Snelle Acties
                    </Heading>
                  </CardHeader>
                  <CardBody pt={0}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <Button
                        leftIcon={<ViewIcon />}
                        colorScheme="orange"
                        size="lg"
                        h="60px"
                        onClick={() => this.props.onRouteChange('overzicht')}
                      >
                        Naar Analyse
                      </Button>
                      <Button
                        leftIcon={<ChatIcon />}
                        colorScheme="green"
                        size="lg"
                        h="60px"
                        onClick={() => this.props.onRouteChange('alleSuggesties')}
                      >
                        Naar Alle Suggesties
                      </Button>

                      <Button
                        leftIcon={<EmailIcon />}
                        colorScheme="purple"
                        size="lg"
                        h="60px"
                        onClick={() => this.props.onRouteChange('berichten')}
                      >
                        Naar Berichten
                      </Button>
                    </SimpleGrid>
                  </CardBody>
                </Card>

              </VStack>
            </Box>
        <Footer onRouteChange={this.props.onRouteChange} />

          </GridItem>
        </Grid>
      
        {/* Mobile Menu Drawer */}
        <MobileMenu
          isOpen={this.state.isMobileMenuOpen}
          onClose={this.handleCloseMobileMenu}
          onRouteChange={this.props.onRouteChange}
          userVersion={this.props.state.userVersion}
          resetSuggestionsPage={this.props.resetSuggestionsPage}
          onLogout={this.props.onLogout}
        />
      </>
    )
  }
}

export default Dashboard
